import * as amplitude from "@amplitude/analytics-browser";
import { Money } from "@redotech/money";
import { useRequiredContext } from "@redotech/react-util/context";
import { useEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  itemInInvalidCollection,
  itemValidForReturns,
  productMatchesTags,
} from "@redotech/redo-shopify-utils/check-product";

import {
  DEFAULT_IMAGE_URL,
  defaultToggleTextOptions,
  fillEmptyFieldsWithDefault,
  makeEmptyToggleTexts,
} from "@redotech/redo-model/default-values";
import { WidgetAutocheck, WidgetFees } from "@redotech/redo-model/widget";
import {
  calculateReturnsPrice,
  getPackageProtectionPrice,
} from "@redotech/redo-shopify-utils/price-calcualations";

import {
  getAllReturnEligibleCountries,
  getInternationalPrice,
} from "@redotech/redo-shopify-utils/country-check";
import { ModalRootContext } from "@redotech/redo-web/modal";
import { getStateCode } from "@redotech/redo-web/state-code";
import type {
  ShopifyCart,
  ShopifyProduct,
  ShopifyProducts,
  ShopifyVariantJS,
} from "@redotech/shopify-client/ajax";
import {
  ExtensionShopifyClient,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProductJS,
} from "@redotech/shopify-client/ajax";
import { binarySearch } from "@redotech/util/array";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { useDebounce } from "usehooks-ts";
import { RedoToggleElement } from "./cart-app";
import { RedoCheckbox } from "./checkbox";
import type { RedoCheckboxElement } from "./checkbox-app";
import { ShopifyExtensionClientContext } from "./client/client-context";
import {
  ShopifyExtensionClient,
  getCollections,
  getProductEstimate,
  getSellingPlan,
} from "./client/shopify-extension-client";
import { useLocationManager } from "./location";
import { TAPCART_SELECTOR, addCheckbox, addEstimate } from "./product-page";
import { RedoRoot } from "./root";
import {
  DEFAULT_CART_COUNT_SELECTOR,
  DEFAULT_PRODUCT_SELECTOR,
  DEFAULT_TOGGLE_CLICK_SELECTOR,
  DEFAULT_TOGGLE_SELECTOR,
} from "./selectors";
import { ExtensionShopifyClientContext, shopifyProductHandle } from "./shopify";
import { DeluxeToggleArray, RedoToggle } from "./toggle";

import { Currency, toCurrency } from "@redotech/money/currencies";
import { CartCard } from "./cart-card";
import { RedoEstimate } from "./delivery-estimate";
import { RedoEstimateElement } from "./delivery-estimate-app";
import { log } from "./log";
import { renderCart } from "./render-cart";
import {
  REDO_DISABLE_TAGS,
  getLocalStorageWithExpiry,
  getMatchingRedoSellingPlanId,
  isPackageProtection,
  isRedoItem,
  setLocalStorageWithExpiry,
} from "./utils";
import { useCart } from "./utils/cart";

export function initialize(
  shopifyClient: ExtensionShopifyClient,
  autocheck: WidgetAutocheck,
  cartEnabled: boolean,
  fees: WidgetFees,
  shopifyProducts: ShopifyProducts,
) {
  if (document.querySelector("#redo-container") || window.blockRedo) {
    return;
  }
  RedoToggleElement.register();
  const redoContainer = document.createElement("div");
  redoContainer.id = "redo-container";
  document.head.appendChild(redoContainer);
  const root = createRoot(redoContainer);
  root.render(
    <RedoRoot
      widgetId={window.redoWidgetId}
      storeUrl={window.Shopify?.shop}
      extensionShopifyClient={shopifyClient}
    >
      <RedoManager
        autocheck={autocheck}
        cartEnabled={cartEnabled}
        fees={fees}
        shopifyProducts={shopifyProducts}
      />
    </RedoRoot>,
  );

  window.removeRedo = () => {
    log("Removing redo");
    root.unmount();
    redoContainer.remove();
  };
}

type SELECTOR_PLACEMENT = "before" | "after" | "prepend" | "append";
const DEFAULT_TOGGLE_PLACEMENT: SELECTOR_PLACEMENT = "before";

const RETURN_TOGGLE_STATE_KEY = "return-toggle-state";
const PROTECTION_TOGGLE_STATE_KEY = "protection-toggle-state";
const SINGLE_TOGGLE_STATE_KEY = "single-toggle-state";
const SELLING_PLANS_ID = "redo-selling-plans";
const RETURN_TYPE = "return";
const PACKAGE_PROTECTION_TYPE = "package_protection";
const BOTH_TYPE = "return,package_protection";
const REDO_REMOVED_REDO = "redo-remove-redo";

let submitButtonSelector = [
  ".redo-submit-button",
  ".picky-add-selected-to-cart-button", // PickyStory bundles
  'form[action="/cart/add"] [type=submit]',
  ".ajax-submit",
  ".js-add-to-cart",
  ".add-to-cart-btn",
  ".product-buy-buttons--cta",
  ".ProductForm__AddToCart", // pcheebum
].join(", ");

const DEFAULT_CART_COUNT_MODIFICATION = {
  selector: DEFAULT_CART_COUNT_SELECTOR,
  search: "\\d+",
  replace: "%count%",
};
interface RedoManagerProps {
  autocheck: WidgetAutocheck;
  cartEnabled: boolean;
  fees: WidgetFees;
  shopifyProducts: ShopifyProducts;
}

const RedoManager = memo(
  ({ autocheck, cartEnabled, fees, shopifyProducts }: RedoManagerProps) => {
    useEffect(() => {
      log("RedoManager mounted");
      return () => {
        log("RedoManager unmounted");
      };
    }, []);

    const locationManager = useLocationManager();
    const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);
    const onTapcart = shopifyClient.type === ShopifyClientType.TAPCART;
    const redoShopifyExtensionClient = useRequiredContext(
      ShopifyExtensionClientContext,
    );
    const merchantPaid = autocheck.paidModel === "merchant_paid";

    useEffect(() => {
      if (!fees || !autocheck.hideRedoProduct?.enabled) {
        return;
      }
      const products = [
        fees.packageProduct,
        fees.returnProduct,
        fees.bothProduct,
      ];
      const productSelector =
        autocheck.hideRedoProduct.productSelector || DEFAULT_PRODUCT_SELECTOR;
      const selectors: string[] = [];
      products.forEach((product) => {
        if (!product) {
          return;
        }
        const variants =
          product.variants.length >= 1
            ? product.variants
            : [{ variant_id: "" }];
        variants.forEach((variant) => {
          selectors.push(
            productSelector
              .replaceAll("%handle%", product.handle || "")
              ?.replaceAll(`%product_id%`, String(product.productId) || "")
              ?.replaceAll(
                "%variant_id%",
                product.variants?.length >= 1 ? String(variant.variant_id) : "",
              ),
          );
        });
      });
      const selector = selectors.filter(Boolean).join(", ");
      if (!selector) {
        return;
      }
      const style = document.createElement("style");
      style.innerText = `${selector} {display: none !important;}`;
      document.head.appendChild(style);
      return () => {
        style.remove();
      };
    }, [fees]);

    // Initialize selling plan for redo state.
    useEffect(() => {
      if (!getLocalStorageWithExpiry(SELLING_PLANS_ID)) {
        setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
      }
    }, []);

    const countryLoad = useLoad(
      async (signal) => {
        if (
          !autocheck.countries?.length &&
          !autocheck.countriesPackageProtection?.length
        ) {
          return null;
        }
        const result =
          (!autocheck.forceIPAddressLocation && globalThis.Shopify?.country) ||
          (await locationManager.getCountry(signal));
        /*
        const predictedCountry = window.Shopify?.country;
        if (
          result.trim().toLowerCase() !== predictedCountry?.trim().toLowerCase()
        ) {
          postLocationDiscrepancy(
            widgetClient,
            { signal },
            result,
            predictedCountry ?? "<empty prediction>",
          );
        }
        */
        return result;
      },
      [autocheck, locationManager],
    );

    const estimateLoad = useLoad(
      async (signal) => {
        if (autocheck?.showPDPDeliveryEstimate) {
          const locationDetails =
            await locationManager.getDetailedLocation(signal);

          return {
            estimate: await getProductEstimate(redoShopifyExtensionClient, {
              signal,
              countryCode: locationDetails?.country,
              provinceCode: getStateCode(locationDetails?.region),
            }),
            location: locationDetails,
          };
        }
      },
      [autocheck, locationManager],
    );

    const returnCountries = getAllReturnEligibleCountries(
      autocheck.countries,
      autocheck.internationalReturnsPricing,
    );
    const validCountry =
      countryLoad.value === null ||
      returnCountries.includes(countryLoad.value!) ||
      returnCountries.length == 0;

    const validCountryPackageProtection =
      countryLoad.value === null ||
      autocheck.countriesPackageProtection.includes(countryLoad.value!) ||
      autocheck.countriesPackageProtection.length === 0;

    const redoLock = useRef(false);
    const checkboxState = useRef(autocheck.redoAutoCheck);
    const [checkedCart, setCheckedCart] = useState<ShopifyCart | undefined>(
      undefined,
    );
    const [redoInCart, setInCart] = useState<boolean | undefined>(undefined);
    const [singleToggleState, setSingleToggleVal] = useState(false);
    const [returnToggleState, setReturnToggleVal] = useState(false);
    const [protectionToggleState, setProtectionToggleVal] = useState(false);
    const [cartEligibleReturns, setCartEligibleReturns] = useState(false);
    const [cartEligibleProtection, setCartEligibleProtection] = useState(false);
    const [numValidReturnProducts, setNumValidReturnProducts] = useState(0);
    const [checkbox, setCheckbox] = useState<RedoCheckboxElement | null>(null);
    const [estimate, setEstimate] = useState<RedoEstimateElement | null>(null);
    const [toggles, setToggles] = useState<RedoToggleElement[]>([]);
    const [foundSelector, setFoundSelector] = useState<string | undefined>(
      undefined,
    );
    const [cartCurrency, setCartCurrency] = useState<Currency>(Currency.USD);
    const [cartRefreshSignal, setCartRefresh] = useState(Symbol());
    const [checkboxRefresh, setCheckboxRefresh] = useState(Symbol());
    const [estimateRefresh, setEstimateRefresh] = useState(Symbol());
    const cartRefresh = useDebounce(cartRefreshSignal, 500);
    const singleToggleVal = useDebounce(singleToggleState, 500);
    const returnToggleVal = useDebounce(returnToggleState, 500);
    const protectionToggleVal = useDebounce(protectionToggleState, 500);
    const cart = useCart(cartRefresh);
    const cartItems = cart?.items;
    const cartChecked = !!cart && cart === checkedCart;
    const cartCheckedOnce = !!checkedCart;

    const checkboxLabelOnly =
      cartEnabled || autocheck.checkoutExtensionEnabled || merchantPaid;
    const hasTwoOptions =
      autocheck.attachmentStrategy === "double-toggle" ||
      autocheck.attachmentStrategy === "drop-down";

    const invalidProductTags = useMemo(
      () =>
        [...(autocheck.finalSaleTags || []), ...REDO_DISABLE_TAGS].map((s) =>
          s.toLowerCase(),
        ),
      [],
    );

    const mainRedoProduct = shopifyProducts.mainShopifyProduct;
    const returnRedoProduct = shopifyProducts.returnShopifyProduct;
    const packageRedoProduct = shopifyProducts.packageShopifyProduct;
    const bothRedoProduct = shopifyProducts.bothShopifyProduct;

    const productTypeMap = new Map<number, string>();

    if (mainRedoProduct && fees) {
      const productTypes = [
        {
          product: mainRedoProduct,
          type: fees.mainType,
        },
        { product: returnRedoProduct, type: RETURN_TYPE },
        { product: packageRedoProduct, type: PACKAGE_PROTECTION_TYPE },
        { product: bothRedoProduct, type: BOTH_TYPE },
      ];
      for (const p of productTypes) {
        if (p.product) productTypeMap.set(p.product.id, p.type);
      }
    }

    function getCorrectlyPricedVariant(
      cartItems: ShopifyCartItem[] | undefined,
      product: ShopifyProductJS,
      productType: string | undefined,
    ): ShopifyVariantJS {
      if (product.variants.length === 1) {
        return product.variants[0];
      }

      let returnCost = 0;
      const baseReturnPrice = onTapcart
        ? product.price_min
        : product.price_min / 100;

      if (autocheck.dynamicReturnsPricing && cartItems) {
        returnCost = calculateReturnsPrice(
          baseReturnPrice,
          autocheck.dynamicReturnsPricing.pricePerAdditonalItem,
          autocheck.dynamicReturnsPricing.maxPrice,
          numValidReturnProducts,
        );
        returnCost = onTapcart ? returnCost : Math.floor(returnCost * 100);
      } else {
        returnCost = product.price_min;
      }

      if (countryLoad.value && autocheck.internationalReturnsPricing) {
        returnCost = getInternationalPrice(
          returnCost,
          countryLoad.value,
          autocheck.countries,
          autocheck.internationalReturnsPricing,
        );
      }

      const isPackageProtectable = (item: ShopifyCartItem) =>
        !isRedoItem(item) && item.requires_shipping;
      let cartValue = onTapcart
        ? window.Tapcart.variables.cart.subtotal
        : cartItems
            ?.filter(isPackageProtectable)
            .reduce((acc, item) => acc + (item.line_price || 0), 0) || 0;

      let calculatedCoveragePrice = 0;
      cartValue = onTapcart ? cartValue : cartValue / 100;

      let packageProtectionCost = getPackageProtectionPrice(
        cartValue,
        autocheck.packageProtection.pricingRuleSets,
        countryLoad.value,
        autocheck.packageProtection.maxPrice,
        autocheck.packageProtection.percentage,
      );
      packageProtectionCost = onTapcart
        ? packageProtectionCost
        : packageProtectionCost * 100;

      let variants: ShopifyVariantJS[] = [];

      if (productType === RETURN_TYPE) {
        calculatedCoveragePrice = returnCost;
        variants = product.variants;
      }

      // Return + package Protection
      else if (productType === BOTH_TYPE) {
        calculatedCoveragePrice =
          returnCost + // This is the base price with no package protection
          packageProtectionCost;
        variants = product.variants.slice(1);
      }
      // Only package protection
      else if (productType === PACKAGE_PROTECTION_TYPE) {
        calculatedCoveragePrice = packageProtectionCost;
        variants = product.variants;
      }

      // Binary search for the variant with the price closest to the calculatedCoveragePrice.
      return binarySearch(variants, calculatedCoveragePrice, (v) => v.price);
    }

    function determineRedoProduct(
      mainRedoProduct: ShopifyProductJS,
      returnRedoProduct: ShopifyProductJS | null | undefined,
      packageRedoProduct: ShopifyProductJS | null | undefined,
      bothRedoProduct: ShopifyProductJS | null | undefined,
      fees: WidgetFees,
      autocheck: WidgetAutocheck,
    ) {
      if (
        fees.splitProducts &&
        autocheck.coverageEnabled &&
        autocheck.packageProtection.coverageEnabled &&
        bothRedoProduct &&
        returnRedoProduct &&
        packageRedoProduct
      ) {
        if (cartEligibleReturns && cartEligibleProtection) {
          mainRedoProduct = bothRedoProduct;
          if (hasTwoOptions) {
            if (returnToggleState && !protectionToggleState) {
              mainRedoProduct = returnRedoProduct;
            } else if (!returnToggleState && protectionToggleState) {
              mainRedoProduct = packageRedoProduct;
            }
          }
        } else if (cartEligibleProtection) {
          mainRedoProduct = packageRedoProduct;
        } else {
          mainRedoProduct = returnRedoProduct;
        }
      }
      return mainRedoProduct;
    }

    const getVariant = useHandler(
      (
        cartItems: ShopifyCartItem[] | undefined,
        mainRedoProduct: ShopifyProductJS,
        returnRedoProduct: ShopifyProductJS | null | undefined,
        packageRedoProduct: ShopifyProductJS | null | undefined,
        bothRedoProduct: ShopifyProductJS | null | undefined,
        fees: WidgetFees,
        autocheck: WidgetAutocheck,
      ) => {
        const product = determineRedoProduct(
          mainRedoProduct,
          returnRedoProduct,
          packageRedoProduct,
          bothRedoProduct,
          fees,
          autocheck,
        );

        return getCorrectlyPricedVariant(
          cartItems,
          product,
          productTypeMap.get(product.id),
        );
      },
    );

    // Finds expected variant every time anything changes.
    const { id: expectedVariantID, price: redoPrice } = useMemo(() => {
      if (!fees || !mainRedoProduct || !cartChecked)
        return { id: null, price: 0 };

      if (checkboxLabelOnly) {
        if (
          fees.splitProducts &&
          autocheck.packageProtection.coverageEnabled &&
          hasTwoOptions
        ) {
          if (!returnToggleState && !protectionToggleState) {
            return { id: 0, price: 0 };
          }
        } else if (!singleToggleState) {
          return { id: 0, price: 0 };
        }
      }

      const variant = getVariant(
        cartItems,
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
      );
      return {
        id: variant.id,
        price: onTapcart ? variant.price : variant.price / 100,
      };
    }, [
      fees,
      mainRedoProduct,
      returnRedoProduct,
      packageRedoProduct,
      bothRedoProduct,
      cartItems,
      redoInCart,
      cartChecked,
      cartEligibleReturns,
      cartEligibleProtection,
      returnToggleVal,
      protectionToggleVal,
      singleToggleVal,
      numValidReturnProducts,
    ]);

    // Finds and stores all places toggles should be rendered on the page
    const renderToggles = useHandler(() => {
      if (!cartEnabled || merchantPaid) {
        return;
      }
      log("Rendering toggles");
      const toggles = Array.from(
        document.querySelectorAll(
          onTapcart
            ? TAPCART_SELECTOR
            : autocheck.cartToggle.cartToggleSelector ||
                DEFAULT_TOGGLE_SELECTOR,
        ),
      ).map((container) => {
        let toggle: RedoToggleElement;
        switch (
          autocheck.cartToggle.cartTogglePlacement ||
          DEFAULT_TOGGLE_PLACEMENT
        ) {
          case "before":
          case "after":
            toggle = container.parentElement?.querySelector(
              RedoToggleElement.TAG,
            ) as RedoToggleElement;
            if (toggle) {
              return toggle;
            }
            break;
          case "prepend":
          case "append":
            toggle = container.querySelector(
              RedoToggleElement.TAG,
            ) as RedoToggleElement;
            if (toggle) {
              return toggle;
            }
            break;
        }
        toggle = document.createElement(
          RedoToggleElement.TAG,
        ) as RedoToggleElement;
        toggle.id = `redo-toggle-${randomHexString(nativeRandom, 8)}`;
        // Split selectors by comma and check if any of them match the container
        const selectors = onTapcart
          ? TAPCART_SELECTOR
          : autocheck.cartToggle.cartToggleSelector || DEFAULT_TOGGLE_SELECTOR;
        const matchingSelector = selectors
          ?.split(",")
          .find((selector) => container.matches(selector));
        setFoundSelector(matchingSelector);

        const onDisconnect = () => {
          log("toggle disconnected");
          toggle.removeEventListener("disconnect", onDisconnect);
          setTimeout(renderToggles, 1000);
        };
        toggle.addEventListener("disconnect", onDisconnect);

        switch (
          autocheck.cartToggle.cartTogglePlacement ||
          DEFAULT_TOGGLE_PLACEMENT
        ) {
          case "before":
            container.insertAdjacentElement("beforebegin", toggle);
            break;
          case "after":
            container.insertAdjacentElement("afterend", toggle);
            break;
          case "prepend":
            container.insertAdjacentElement("afterbegin", toggle);
            break;
          case "append":
            container.insertAdjacentElement("beforeend", toggle);
            break;
        }
        return toggle;
      });

      setToggles(toggles);
    });

    const addRedo = useHandler(async (event?: Event, force = false) => {
      if (
        merchantPaid ||
        (redoInCart && !force) ||
        redoLock.current === true ||
        !expectedVariantID
      ) {
        return;
      }
      try {
        redoLock.current = true;
        setInCart(true);
        const cartParams = new URLSearchParams();
        if (!onTapcart) {
          switch (Shopify?.shop) {
            case "birdiethreads.myshopify.com":
              cartParams.append("ref_icart", "true");
              break;
            case "thedressoutlet.myshopify.com":
              cartParams.append("sections", "cart-drawer,cart-icon-bubble");
              break;
          }

          if (
            Shopify?.shop === "the-vintage-gentlemen.myshopify.com" &&
            window.Rebuy
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            Rebuy.Cart.addItem({
              id: expectedVariantID,
              quantity: 1,
            });
            redoLock.current = false;
            return;
          }
        }
        log("adding redo", { expectedVariantID });
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, false);

        event?.preventDefault();
        event?.stopPropagation();
        await shopifyClient.cartAdd(
          {
            items: [{ id: expectedVariantID, quantity: 1 }],
          },
          cartParams,
        );

        if (!onTapcart) {
          renderCart();
        }

        if (!event) {
          return;
        }

        console.debug(`Resubmitting event ${event.type}`);
        if (
          typeof Rebuy !== "undefined" ||
          Shopify?.shop === "megalashacademy.myshopify.com"
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        if (event.target instanceof HTMLElement && event.type === "click") {
          event.target.click();
        } else {
          event.target!.dispatchEvent(new Event(event.type));
        }
      } catch (e) {
        console.error("Error adding redo to the cart", e);
        // Refresh the cart so we know if it's actually in there or not
        setCartRefresh(Symbol());
      } finally {
        redoLock.current = false;
      }
    });

    const onToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        log("toggle changed", { value, manual });
        setSingleToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, state);
      },
    );

    const onReturnToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        log("return toggle changed", { value, manual });
        setReturnToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, state);
      },
    );

    const onProtectionToggleChange = useHandler(
      (value: boolean, manual: boolean = true) => {
        log("package protection toggle changed", { value, manual });
        setProtectionToggleVal(value);
        const state = value ? "enabled" : "disabled";
        setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, state);
      },
    );

    const onCheckboxChange = useHandler((value: boolean) => {
      log("checkbox changed", { value });
      checkboxState.current = value;
    });

    const onModalAdd = useHandler(async () => {
      if (!redoInCart) {
        await addRedo();
      }
    });

    const ensurePromise = useRef<Promise<any> | null>(null);
    // Makes sure the correct variant of redo is in the cart.
    useLoad(
      async (signal) => {
        while (ensurePromise.current !== null) {
          try {
            await ensurePromise.current;
          } catch (e) {
            // Who cares
          }
        }
        if (!cartChecked || expectedVariantID === null || !checkboxLabelOnly) {
          return;
        }

        let cartChanged = false;
        ensurePromise.current = (async () => {
          log("Ensuring redo", { expectedVariantID });
          // Block checkout while we're making sure the correct variant is in the cart.
          let checkoutIntercepted = false;
          const checkoutIntercepter = autocheck.checkoutSelector
            ? (event: Event) => {
                if (
                  event.target instanceof Element &&
                  (event.target.matches(autocheck.checkoutSelector!) ||
                    event.target.closest(autocheck.checkoutSelector!))
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  checkoutIntercepted = true;
                  log("Checkout intercepted");
                }
              }
            : null;

          if (checkoutIntercepter) {
            log("Attaching checkout intercepter");
            document.addEventListener("click", checkoutIntercepter, {
              capture: true,
            });
          }
          try {
            let foundCorrectVariant = false;
            const cart = await shopifyClient.cartGet(signal);
            for (const item of cart.items) {
              try {
                if (isRedoItem(item)) {
                  // Remove any incorrect variants
                  if (item.variant_id !== expectedVariantID) {
                    cartChanged = true;
                    log("removing incorrect variant", { item });
                    await shopifyClient.removeItemFromCart(
                      item,
                      item.quantity,
                      signal,
                    );
                    if (
                      (getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) ===
                        "enabled" ||
                        getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) ===
                          "enabled" ||
                        getLocalStorageWithExpiry(
                          PROTECTION_TOGGLE_STATE_KEY,
                        ) === "enabled") &&
                      expectedVariantID === 0
                    ) {
                      setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
                    }
                  } else {
                    foundCorrectVariant = true;
                    if (item.quantity > 1) {
                      // If there is more than one, make sure to fix that.
                      log("updating variant quantity", { item });
                      cartChanged = true;
                      await shopifyClient.removeItemFromCart(
                        item,
                        item.quantity - 1,
                        signal,
                      );
                    }
                  }
                }
              } catch (e) {
                console.error("Error in ensure product", e);
              }
            }
            if (!foundCorrectVariant && expectedVariantID !== 0) {
              cartChanged = true;
              await addRedo(undefined, true);
            }
            if (cartChanged) {
              renderCart();
            }
          } finally {
            if (checkoutIntercepter) {
              if (checkoutIntercepted) {
                // Wait for 500ms for the cart to get re-rendered and then click the checkout button
                await new Promise((resolve) => setTimeout(resolve, 500));
                log("Removing checkout intercepter");
                document.removeEventListener("click", checkoutIntercepter, {
                  capture: true,
                });
                const checkoutElements = document.querySelectorAll(
                  autocheck.checkoutSelector!,
                );
                // Just in case there are multiple checkout buttons that match the selector,
                // find the first one that we can click on.
                for (const element of checkoutElements) {
                  if (element instanceof HTMLElement) {
                    element.click();
                    break;
                  }
                }
              } else {
                log("Removing checkout intercepter");
                document.removeEventListener("click", checkoutIntercepter, {
                  capture: true,
                });
              }
            }
          }
        })();

        try {
          return await ensurePromise.current;
        } catch (e) {
          log("Error in ensureRedoLoad", e);
        } finally {
          log("Finished ensuring redo", { expectedVariantID });
          ensurePromise.current = null;
        }
      },
      [expectedVariantID, cartItems],
    );

    // Check current cart and render toggles.
    const cartCheckLoad = useLoad(
      async (signal) => {
        if (!fees || !mainRedoProduct || !cart) {
          log("skipping cart check", {
            fees,
            mainRedoProduct,
            cart,
          });
          return;
        }
        setCartCurrency(toCurrency(cart.currency) ?? Currency.USD);
        log("checking cart items", { cart });
        // Check if the cart has actually changed.
        let inCart = !!redoInCart;
        const inCartLastCheck = redoInCart === undefined ? true : !!redoInCart;
        let hasValidReturnProduct = cartEligibleReturns;
        let hasValidProtectionProduct = cartEligibleProtection;
        const redoItems: ShopifyCartItem[] = [];
        const hasPackageAndSplit =
          autocheck.packageProtection.coverageEnabled &&
          autocheck.packageProtection.splitProducts;
        // Checks if cart line items have changed or if any selling plans have changed.

        // Set these to false until we actually check the cart.
        inCart = false;
        hasValidReturnProduct = false;
        let hasInvalidreturnProduct = false;
        let numberValidreturnProducts = 0;
        hasValidProtectionProduct = false;
        if (cart?.items?.length) {
          for (const item of cart.items) {
            if (isRedoItem(item)) {
              inCart = true;
              redoItems.push(item);
              continue;
            }

            // don't skip if we have this setting or if we're using dynamic pricing
            if (
              !autocheck.excludeCoverageIfAnyInvalidProduct &&
              !autocheck.dynamicReturnsPricing
            ) {
              // No need to check product tags if we already have one valid product.
              if (
                (hasValidReturnProduct && hasValidProtectionProduct) ||
                !item.handle
              ) {
                continue;
              }
            }

            // Don't offer coverage for digital products
            if (!item.requires_shipping) {
              continue;
            }
            hasValidProtectionProduct = true;

            if (
              await itemValidForReturnsFrontend({
                autocheck,
                item,
                redoShopifyExtensionClient,
                shopifyClient,
                signal,
                invalidProductTags,
              })
            ) {
              numberValidreturnProducts += item.quantity;
              hasValidReturnProduct = true;
            } else {
              hasInvalidreturnProduct = true;
            }
          }

          if (autocheck.excludeCoverageIfAnyInvalidProduct) {
            hasValidReturnProduct =
              hasValidReturnProduct && !hasInvalidreturnProduct;
            hasValidProtectionProduct =
              hasValidProtectionProduct && !hasInvalidreturnProduct;
          }
          setNumValidReturnProducts(numberValidreturnProducts);
        }
        if (signal.aborted) {
          return;
        }

        log("cart items checked", {
          inCart,
          redoItems,
          hasValidReturnProduct,
          hasValidProtectionProduct,
        });
        if (!redoItems.length) {
          setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
        }

        if (inCart && redoItems.length) {
          let localStorageSellingPlans: any =
            getLocalStorageWithExpiry(SELLING_PLANS_ID) || {};

          const sellingPlanPromises = cart.items
            .filter((item) => !isRedoItem(item))
            .filter((item) => item.selling_plan_allocation?.selling_plan?.id)
            .map(
              async (item) =>
                await getSellingPlan(redoShopifyExtensionClient, {
                  sellingPlanId: item.selling_plan_allocation!.selling_plan!.id,
                }),
            );

          const sellingPlans = await Promise.all(sellingPlanPromises);
          for (const redoItem of redoItems) {
            // find the same item in fees
            const feesItem = [
              fees.returnProduct,
              fees.packageProduct,
              fees.bothProduct,
            ].find((e) => e?.productId === redoItem.product_id);

            if (!feesItem) {
              continue;
            }

            const sellingPlanId = await getMatchingRedoSellingPlanId(
              feesItem,
              sellingPlans,
            );

            const redoIndexInCart = cart.items.findIndex(
              (e) => e.id === redoItem?.id,
            );
            const redoHasSellingPlan =
              !!redoItem?.selling_plan_allocation?.selling_plan?.id;
            const redoRemovedSellingPlan =
              localStorageSellingPlans[redoItem.product_id] === "removed";

            // If the customer removes the selling plan, we will skip updates
            if (
              sellingPlanId &&
              !redoHasSellingPlan &&
              !redoRemovedSellingPlan &&
              localStorageSellingPlans[redoItem.product_id]
            ) {
              continue;
            }
            // If there is a selling plan for the item, add it to redo and add it to local storage
            // If the customer updates their selling plan, we will update the redo item and the local storage
            if (sellingPlanId) {
              localStorageSellingPlans = {
                ...localStorageSellingPlans,
                [redoItem.product_id]: sellingPlanId,
              };

              setLocalStorageWithExpiry(
                SELLING_PLANS_ID,
                localStorageSellingPlans,
              );

              await shopifyClient.updateCartItemSellingPlan(
                redoIndexInCart + 1,
                sellingPlanId,
                1,
              );
              // If there is no selling plan for the item but redo has a selling plan, remove it from redo and local storage
            } else if (redoHasSellingPlan) {
              await shopifyClient.updateCartItemSellingPlan(
                redoIndexInCart + 1,
                null,
                1,
              );
            }

            if (
              !sellingPlanId &&
              localStorageSellingPlans[redoItem.product_id]
            ) {
              localStorageSellingPlans = {
                ...localStorageSellingPlans,
                [redoItem.product_id]: "removed",
              };

              setLocalStorageWithExpiry(
                SELLING_PLANS_ID,
                localStorageSellingPlans,
              );
            }
          }
        }
        const returnState =
          getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "enabled";
        const protectionState =
          getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "enabled";
        const singleState =
          getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "enabled";

        let redoForciblyRemoved = false;
        let hasEligibleProduct = false;

        const returnsEligibleAndCovered =
          hasValidReturnProduct && autocheck.coverageEnabled && validCountry;
        const packageProtectionEligibleAndCovered =
          autocheck.packageProtection?.coverageEnabled &&
          hasValidProtectionProduct &&
          validCountryPackageProtection;

        if (
          !autocheck.packageProtection?.splitProducts &&
          autocheck.coverageEnabled &&
          autocheck.packageProtection?.coverageEnabled
        ) {
          hasEligibleProduct =
            returnsEligibleAndCovered && packageProtectionEligibleAndCovered;
        } else {
          hasEligibleProduct =
            returnsEligibleAndCovered || packageProtectionEligibleAndCovered;
        }

        // Make sure redo isn't in cart if no eligible products are in cart
        if (!hasEligibleProduct) {
          setSingleToggleVal(false);
          setProtectionToggleVal(false);
          setReturnToggleVal(false);
        }

        const redoWasRemoved = !inCart && hasEligibleProduct && inCartLastCheck;
        const redoWasRemovedByRedo =
          getLocalStorageWithExpiry(REDO_REMOVED_REDO);
        if (hasPackageAndSplit && hasTwoOptions) {
          if ((returnState || protectionState) && redoWasRemoved) {
            redoForciblyRemoved = true;
          }
        } else if (singleState && redoWasRemoved && !redoWasRemovedByRedo) {
          redoForciblyRemoved = true;
        }

        // If redo was removed by customer set all to false
        if (redoForciblyRemoved && !ensurePromise.current) {
          log("redo was forcibly removed");
          if (hasPackageAndSplit && hasTwoOptions) {
            if (returnToggleVal) {
              setReturnToggleVal(false);
              setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "disabled");
            }
            if (protectionToggleVal) {
              setProtectionToggleVal(false);
              setLocalStorageWithExpiry(
                PROTECTION_TOGGLE_STATE_KEY,
                "disabled",
              );
            }
          } else {
            if (singleToggleVal) {
              setSingleToggleVal(false);
              setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "disabled");
            }
          }
        }

        const returnDisabled =
          getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "disabled";
        const protectionDisabled =
          getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "disabled";
        const singleDisabled =
          getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "disabled";

        setCartEligibleReturns(returnsEligibleAndCovered);
        setCartEligibleProtection(packageProtectionEligibleAndCovered);

        const bothInCart =
          fees.bothProduct?.productId === redoItems[0]?.product_id;
        const returnInCart =
          bothInCart ||
          fees.returnProduct?.productId === redoItems[0]?.product_id;
        const packageInCart =
          bothInCart ||
          fees.packageProduct?.productId === redoItems[0]?.product_id;

        if (cartEnabled && !redoForciblyRemoved) {
          // if cart is eligible for adding a redo item add it by changing toggles
          if (
            hasPackageAndSplit &&
            autocheck.coverageEnabled &&
            hasTwoOptions
          ) {
            if (
              hasValidReturnProduct &&
              !returnToggleVal &&
              (returnInCart || (autocheck.redoAutoCheck && !returnDisabled))
            ) {
              setReturnToggleVal(true);
              setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "enabled");
            }
            if (
              hasValidProtectionProduct &&
              !protectionToggleVal &&
              (packageInCart ||
                (autocheck.packageProtectionAutoCheck && !protectionDisabled))
            ) {
              setProtectionToggleVal(true);
              setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, "enabled");
            }
          } else if (
            hasEligibleProduct &&
            !singleToggleVal &&
            (inCart || (autocheck.redoAutoCheck && !singleDisabled))
          ) {
            setSingleToggleVal(true);
            setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "enabled");
          }
        }

        setInCart(inCart);
        setCheckedCart(cart);
      },
      [
        cartItems,
        fees,
        validCountry,
        validCountryPackageProtection,
        mainRedoProduct,
      ],
    );

    useEffect(() => {
      if (cartEligibleReturns || cartEligibleProtection) {
        renderToggles();
      }
    }, [cart, cartRefresh, cartEligibleReturns, cartEligibleProtection]);

    useEffect(() => {
      if (cartCheckLoad.error) {
        log("error checking cart", { error: cartCheckLoad.error });
      }
    }, [cartCheckLoad.error]);

    const getFormatter = (currency: Currency, removeCents: boolean) => {
      return new Intl.NumberFormat("en-US", {
        currency: currency,
        minimumFractionDigits: removeCents ? 0 : 2,
        maximumFractionDigits: removeCents ? 0 : 2,
      });
    };

    // Update the cart counters whenever the cart items change.
    useEffect(() => {
      if (!autocheck.hideRedoProduct?.enabled || !cartItems?.length) {
        return;
      }

      const count = cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + item.quantity;
      }, 0);

      const line_item_count = cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + 1;
      }, 0);

      const cartCountModifications =
        (autocheck.hideRedoProduct?.cartCountModifications?.length ?? 0) > 0
          ? autocheck.hideRedoProduct?.cartCountModifications || [
              DEFAULT_CART_COUNT_MODIFICATION,
            ]
          : [DEFAULT_CART_COUNT_MODIFICATION];

      for (const mod of cartCountModifications) {
        const search = new RegExp(mod.search, "g");
        const replace = mod.replace
          .replaceAll("%count%", count.toString())
          .replaceAll("%line_item_count%", line_item_count.toString());
        const elements = document.querySelectorAll(mod.selector);
        for (const element of elements) {
          element.innerHTML = element.innerHTML.replace(search, replace);
          // Jack's Surf Shop
          if (element.hasAttribute("data-header-cart-count")) {
            element.setAttribute("data-header-cart-count", count.toString());
          }
        }
      }

      const cart_subtotal =
        cartItems.reduce((acc, item) => {
          if (isRedoItem(item) || isPackageProtection(item)) {
            return acc;
          }
          return acc + (item.line_price || 0);
        }, 0) / 100;
      const redo_subtotal =
        cartItems.reduce((acc, item) => {
          if (isRedoItem(item)) {
            return acc + (item.line_price || 0);
          }
          return acc;
        }, 0) / 100;

      const cartPriceModifications =
        (autocheck.hideRedoProduct?.cartPriceModifications?.length ?? 0) > 0
          ? autocheck.hideRedoProduct?.cartPriceModifications || []
          : [];

      for (const mod of cartPriceModifications) {
        const search = new RegExp(mod.search, "g");
        const removeCents = mod.replace.includes("round");
        const includeRedo = mod.replace.includes("withRedo");
        const replace = mod.replace.replaceAll(
          /%cart-subtotal.*%/g,
          getFormatter(cartCurrency, removeCents).format(
            cart_subtotal + (includeRedo ? redo_subtotal : 0),
          ),
        );
        const elements = document.querySelectorAll(mod.selector);
        for (const element of elements) {
          element.innerHTML = element.innerHTML.replace(search, replace);
        }
      }

      const cart_discount =
        cartItems.reduce((acc, item) => {
          if (isRedoItem(item) || isPackageProtection(item)) {
            return acc;
          }
          return (
            acc +
            (item.discounts?.reduce((acc2, discount) => {
              return acc2 + discount?.amount || 0;
            }, 0) || 0)
          );
        }, 0) / 100;

      const cartDiscountModifications =
        (autocheck.hideRedoProduct?.cartDiscountModifications?.length ?? 0) > 0
          ? autocheck.hideRedoProduct?.cartDiscountModifications || []
          : [];

      for (const mod of cartDiscountModifications) {
        const search = new RegExp(mod.search, "g");
        const removeCents = mod.replace.includes("round");
        const replace = mod.replace.replaceAll(
          /%cart-discount.*%/g,
          getFormatter(cartCurrency, removeCents).format(cart_discount),
        );
        const elements = document.querySelectorAll(mod.selector);
        for (const element of elements) {
          element.innerHTML = element.innerHTML.replace(search, replace);
        }
      }
    }, [cartItems, cartRefresh]);

    // Listen for click events and refresh toggles.
    useEventListener(
      document,
      "click",
      (e: Event) => {
        if (
          e.target instanceof Element &&
          // Check if the click was one of the elements that could trigger a cart change or open.
          ((autocheck.cartToggle.cartClickListenerSelector &&
            e.target.matches(autocheck.cartToggle.cartClickListenerSelector)) ||
            e.target.matches(DEFAULT_TOGGLE_CLICK_SELECTOR))
        ) {
          setTimeout(() => setCartRefresh(Symbol()), 500);
          setTimeout(() => setCartRefresh(Symbol()), 2000);
        }
      },
      true,
    );

    // Handle navigation using Temporal.InstantClick which doesn't actually reload the page.
    // This will trigger the checkbox to be added if it needs to be, as well as re-rendering the toggle.
    useEffect(() => {
      if (window.InstantClick) {
        window.InstantClick.on("change", () => {
          setCheckboxRefresh(Symbol());
          setEstimateRefresh(Symbol());
          setCartRefresh(Symbol());
        });
      }
    }, []);

    const isOnProductPage = () => {
      return onTapcart
        ? !!window.Tapcart.variables.product
        : location.href.includes("/products/");
    };

    // Add checkbox if on product page
    useEffect(() => {
      if (document.querySelector("redo-checkbox") || isOnProductPage()) {
        if (autocheck.submitButtonSelector) {
          submitButtonSelector += `, ${autocheck.submitButtonSelector}`;
        }
        setCheckbox(addCheckbox(autocheck, onTapcart));
      } else if (checkbox) {
        setCheckbox(null);
      }
    }, [checkboxRefresh]);

    // Add estimate if on product page
    useEffect(() => {
      if (document.querySelector("redo-estimate") || isOnProductPage()) {
        setEstimate(addEstimate(autocheck, onTapcart));
      }
    }, [estimateRefresh, estimateLoad.value]);
    // what is this?
    const validProductLoad = useLoad(
      async (signal) => {
        if (
          checkbox &&
          (document.querySelector("redo-checkbox") || isOnProductPage())
        ) {
          const handle = onTapcart
            ? window.Tapcart.variables.product.handle
            : shopifyProductHandle(location.href);
          if (!handle) {
            return false;
          }
          const product = await shopifyClient.productGet(handle, signal);

          const collections = await getCollections(redoShopifyExtensionClient, {
            signal,
            productId: String(product.id),
          });

          const excludeByCollection: boolean = itemInInvalidCollection(
            collections,
            autocheck.coverageExcludeCollections,
          );

          const productTags = onTapcart
            ? window.Tapcart.variables.product.tags
            : getTags(await shopifyClient.productGet(handle, signal));
          return (
            !productMatchesTags(productTags, invalidProductTags) &&
            !excludeByCollection
          );
        }
        return false;
      },
      [checkbox],
    );
    useEffect(() => {
      if (validProductLoad.error) {
        console.error(validProductLoad.error);
      }
    }, [validProductLoad.error]);

    const showCheckbox =
      (checkboxLabelOnly || !redoInCart) &&
      validProductLoad.value === true &&
      (cartCheckedOnce || merchantPaid) &&
      validCountry;
    // Add checkbox event handlers
    useEffect(() => {
      if (!checkbox || !showCheckbox || checkboxLabelOnly) {
        return;
      }

      // Boolean to make sure we only add redo once for these handlers.
      // Once we add it, they will all be removed on the next re-render
      // because showCheckbox will be false.
      let added = false;
      const form = checkbox.closest("form");
      async function formEventHandler(e: SubmitEvent) {
        if (checkboxState.current && !added) {
          added = true;
          await addRedo(e);
        }
      }
      async function rebuyHandler(e: any): Promise<void> {
        if (checkboxState.current && !added) {
          added = true;
          // Don't add redo when we're already adding redo.
          if (e?.detail?.item?.variant_id === fees?.variantId) {
            return;
          }
          await addRedo();
        }
      }
      async function clickEventHandler(e: MouseEvent): Promise<void> {
        if (
          e.target instanceof Element &&
          Shopify?.theme.name === "Dawn" &&
          e.target.closest(".shopify-payment-button__button")
        ) {
          // Buy it now button for the dawn theme
          const quantityElement = document.querySelector(
            '.quantity__input, input[type="number"]',
          ) as HTMLInputElement | null;
          const quantity = quantityElement?.value || "1";
          const currentSelectedVariant = (
            document.querySelector(
              ".product__pickup-availabilities",
            ) as HTMLElement
          ).dataset.variantId;
          e.preventDefault();
          e.stopPropagation();
          window.location.href = `https://${
            Shopify.shop
          }/cart/${currentSelectedVariant}:${quantity},${
            fees!.variantId
          }:1?traffic_source=buy_now`;
        } else if (
          e.target instanceof Element &&
          e.target.closest(submitButtonSelector) &&
          checkboxState.current &&
          !added
        ) {
          added = true;
          await addRedo(e);
        }
      }

      form?.addEventListener("submit", formEventHandler);
      document.addEventListener("rebuy:cart.add", rebuyHandler);
      document.addEventListener("click", clickEventHandler, true);

      return () => {
        form?.removeEventListener("submit", formEventHandler);
        document.removeEventListener("rebuy:cart.add", rebuyHandler);
        document.removeEventListener("click", clickEventHandler);
      };
    }, [checkbox, showCheckbox, cartEnabled]);

    if (!fees) {
      return <></>;
    }

    if (
      !merchantPaid &&
      (autocheck.disableRedoForCustomer ||
        (autocheck.hideCheckbox &&
          !autocheck.cartToggle.enabled &&
          !onTapcart) ||
        !autocheck.enabled ||
        (autocheck.hideCheckbox && !cartCheckedOnce))
    ) {
      log("returning empty", {
        merchantPaid,
        disableRedoForCustomer: autocheck.disableRedoForCustomer,
        hideCheckbox: autocheck.hideCheckbox,
        cartToggleEnabled: autocheck.cartToggle.enabled,
        enabled: autocheck.enabled,
        cartChecked,
      });
      return <></>;
    }

    const hasProtectionReturnsAndSplit =
      autocheck.packageProtection.coverageEnabled &&
      autocheck.coverageEnabled &&
      autocheck.packageProtection.splitProducts;

    // determine toggle fields

    let toggleText = autocheck.checkboxText;
    const allToggleOptions = makeEmptyToggleTexts();
    const defaults = defaultToggleTextOptions(autocheck.coverage);

    if (autocheck.toggleTextOptions) {
      allToggleOptions.returnToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.returnToggle,
        defaults.returnToggle,
      );
      allToggleOptions.packageProtectionToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.packageProtectionToggle,
        defaults.packageProtectionToggle,
      );
      allToggleOptions.bothProductToggle = fillEmptyFieldsWithDefault(
        autocheck.toggleTextOptions.bothProductToggle,
        defaults.bothProductToggle,
      );
    }

    let singleToggleFields;
    if (fees.mainType === "return") {
      singleToggleFields = allToggleOptions.returnToggle;
    } else if (fees.mainType === "return,package_protection") {
      singleToggleFields = allToggleOptions.bothProductToggle;
    } else if (fees.mainType === "package_protection") {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    }

    if (hasProtectionReturnsAndSplit) {
      if (cartEligibleReturns && cartEligibleProtection) {
        singleToggleFields = allToggleOptions.bothProductToggle;
      } else if (cartEligibleProtection) {
        singleToggleFields = allToggleOptions.packageProtectionToggle;
      } else {
        singleToggleFields = allToggleOptions.packageProtectionToggle;
      }
    }

    if (
      !autocheck.checkboxText &&
      !(
        singleToggleFields.titleText === defaults.bothProductToggle.titleText ||
        singleToggleFields.titleText === defaults.returnToggle.titleText ||
        singleToggleFields.titleText ===
          defaults.packageProtectionToggle.titleText
      )
    ) {
      toggleText = singleToggleFields.titleText;
    }

    if (fees.splitProducts || autocheck.packageProtection.coverageEnabled) {
      toggleText = singleToggleFields.titleText;
    }

    const image_url = autocheck.logo || DEFAULT_IMAGE_URL;
    const priceDivisor = onTapcart ? 1 : 100;

    let showToggles = false;
    const hasBothCoverage =
      autocheck.coverageEnabled && autocheck.packageProtection?.coverageEnabled;

    if (hasBothCoverage && !autocheck.packageProtection?.splitProducts) {
      showToggles = cartEligibleReturns && cartEligibleProtection;
    } else {
      showToggles = cartEligibleReturns || cartEligibleProtection;
    }

    let cartReactElement = <></>;

    if (showToggles && mainRedoProduct) {
      const singleProduct = determineRedoProduct(
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
      );
      const singleProductPrice =
        getCorrectlyPricedVariant(
          cartItems,
          singleProduct,
          productTypeMap.get(singleProduct.id),
        ).price / priceDivisor;

      if (
        autocheck.attachmentStrategy === "single-toggle" ||
        (!autocheck.attachmentStrategy && autocheck.cartToggle.enabled === true)
      ) {
        const deluxeVar = autocheck.toggleSubtextEnabled ?? false;
        const toggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            html={autocheck.html}
            fontSize={autocheck.toggleFontSize}
            value={singleToggleState}
            paymentModel={autocheck.paidModel}
            price={new Money(singleProductPrice || +fees.price, cartCurrency)}
            branding={!autocheck.hideCheckboxBranding}
            onChange={onToggleChange}
            toggleText={toggleText}
            toggleFields={singleToggleFields}
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            isDelux={deluxeVar}
            customCss={autocheck.customToggleCss}
            foundSelector={foundSelector}
          />
        );

        if (deluxeVar) {
          cartReactElement = (
            <DeluxeToggleArray
              image_url={image_url}
              foundSelector={foundSelector}
            >
              {toggle}
            </DeluxeToggleArray>
          );
        } else {
          cartReactElement = toggle;
        }
      } else if (
        autocheck.attachmentStrategy == "double-toggle" &&
        hasProtectionReturnsAndSplit
      ) {
        let returnToggle = <></>;
        let packageProtectionToggle = <></>;

        if (cartEligibleReturns && returnRedoProduct) {
          const price =
            getCorrectlyPricedVariant(cartItems, returnRedoProduct, RETURN_TYPE)
              .price / priceDivisor;
          returnToggle = (
            <RedoToggle
              color={autocheck.textColor}
              coverage={autocheck.coverage}
              html={autocheck.html}
              fontSize={autocheck.toggleFontSize}
              value={returnToggleState}
              paymentModel={autocheck.paidModel}
              price={new Money(price || +fees.price, cartCurrency)}
              branding={!autocheck.hideCheckboxBranding}
              onChange={onReturnToggleChange}
              toggleText={"returns"}
              toggleFields={allToggleOptions.returnToggle}
              toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
              isDelux={true}
              customCss={autocheck.customToggleCss}
            />
          );
        }

        if (cartEligibleProtection && packageRedoProduct) {
          const price =
            getCorrectlyPricedVariant(
              cartItems,
              packageRedoProduct,
              PACKAGE_PROTECTION_TYPE,
            ).price / priceDivisor;
          packageProtectionToggle = (
            <RedoToggle
              color={autocheck.textColor}
              coverage={autocheck.coverage}
              html={autocheck.html}
              fontSize={autocheck.toggleFontSize}
              value={protectionToggleState}
              paymentModel={autocheck.paidModel}
              price={new Money(price || +fees.price, cartCurrency)}
              branding={!autocheck.hideCheckboxBranding}
              onChange={onProtectionToggleChange}
              toggleText={"Package Protection"}
              toggleFields={allToggleOptions.packageProtectionToggle}
              toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
              isDelux={true}
              customCss={autocheck.customToggleCss}
            />
          );
        }

        cartReactElement = (
          <>
            <DeluxeToggleArray
              image_url={image_url}
              foundSelector={foundSelector}
            >
              {returnToggle}
              {packageProtectionToggle}
            </DeluxeToggleArray>
          </>
        );
      } else if (autocheck.attachmentStrategy === "cart-card") {
        cartReactElement = (
          <CartCard
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            html={autocheck.html}
            fontSize={autocheck.toggleFontSize}
            value={singleToggleState}
            paymentModel={autocheck.paidModel}
            price={new Money(singleProductPrice || +fees.price, cartCurrency)}
            branding={!autocheck.hideCheckboxBranding}
            onChange={onToggleChange}
            toggleFields={singleToggleFields}
            customCss={autocheck.customToggleCss}
            image_url={image_url}
          ></CartCard>
        );
      }
    }

    fireReady();
    return (
      <>
        <>
          {checkbox && showCheckbox
            ? createPortal(
                <ModalRootContext.Provider value={checkbox.modalContainer}>
                  <RedoCheckbox
                    color={autocheck.textColor}
                    coverage={autocheck.coverage}
                    modalTextAdjustments={autocheck.modalTextAdjustments}
                    fontSize={autocheck.checkboxFontSize}
                    html={autocheck.html}
                    initial={autocheck.redoAutoCheck}
                    paymentModel={autocheck.paidModel}
                    price={
                      !autocheck.packageProtection.coverageEnabled
                        ? new Money(redoPrice || +fees.price, cartCurrency)
                        : undefined
                    }
                    branding={!autocheck.hideCheckboxBranding}
                    onAdd={onModalAdd}
                    onChange={onCheckboxChange}
                    checkboxText={autocheck.checkboxText}
                    checkboxTextAfterRedo={autocheck.checkboxTextAfterRedo}
                    hideCheckbox={onTapcart ? false : autocheck.hideCheckbox}
                    labelOnly={checkboxLabelOnly}
                    infoLink={autocheck.infoIconLink}
                    showInfo={!onTapcart && autocheck.coverageEnabled}
                    customCss={autocheck.customPdpCss}
                  />
                </ModalRootContext.Provider>,
                checkbox.root,
              )
            : null}
          {!(!!onTapcart && isOnProductPage()) &&
            showToggles &&
            toggles.map((toggle) =>
              createPortal(cartReactElement, toggle.root, toggle.id),
            )}
        </>
        <>
          {autocheck?.showPDPDeliveryEstimate &&
          estimate &&
          estimateLoad?.value &&
          estimateLoad.value?.estimate?.minDate &&
          estimateLoad.value?.estimate?.maxDate &&
          estimateLoad.value?.location
            ? createPortal(
                <ModalRootContext.Provider value={estimate.modalContainer}>
                  <RedoEstimate
                    client={redoShopifyExtensionClient}
                    color={autocheck.textColor}
                    customCss={autocheck.customPdpCss}
                    zipCode={estimateLoad.value.location.postalCode}
                    country={estimateLoad.value.location.country}
                    city={estimateLoad.value.location.city}
                    region={estimateLoad.value.location.region}
                    minDate={estimateLoad.value.estimate.minDate}
                    maxDate={estimateLoad.value.estimate.maxDate}
                  />
                </ModalRootContext.Provider>,
                estimate.root,
              )
            : null}
        </>
      </>
    );
  },
);

function fireReady() {
  if (localStorage.getItem("redo-ready")) {
    return;
  }
  try {
    localStorage.setItem("redo-ready", "true");
    performance.mark("redo-ready");
    const timings = performance.measure(
      "redo-ready",
      "domInteractive",
      "redo-ready",
    );
    amplitude.logEvent("redo-ready", {
      time: timings.duration,
    });
  } catch (e) {
    console.warn(e);
  }
}

const getTags = (product: ShopifyProduct) => {
  return product.tags.toLowerCase().split(/, */).filter(Boolean);
};

async function itemValidForReturnsFrontend({
  autocheck,
  item,
  redoShopifyExtensionClient,
  shopifyClient,
  signal,
  invalidProductTags,
}: {
  autocheck: WidgetAutocheck;
  item: ShopifyCartItem;
  redoShopifyExtensionClient: ShopifyExtensionClient;
  shopifyClient: ExtensionShopifyClient;
  signal?: AbortSignal;
  invalidProductTags: string[];
}): Promise<boolean> {
  if (location?.host === "checkout.denimtears.com") {
    return true;
  }

  const storageValue = await getLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
  );

  if (storageValue === "true") {
    return true;
  } else if (storageValue === "false") {
    return false;
  }

  const product = await shopifyClient.productGet(item.handle, signal);
  const productTags = getTags(product);

  const collections = await getCollections(redoShopifyExtensionClient, {
    signal,
    productId: String(item.product_id),
  });

  const properties: string[] = item.properties
    ? Object.keys(item.properties)
    : [];

  const hasValidReturnProduct = itemValidForReturns({
    coverageExcludeProperties: autocheck.coverageExcludeProperties,
    coverageExcludeCollections: autocheck.coverageExcludeCollections,
    invalidProductTags,
    itemInfo: { properties, productTags, collections },
  });

  setLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
    String(hasValidReturnProduct),
  );
  return hasValidReturnProduct;
}
